import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { WhatsAppSendHealthError } from '@/Domain/api/WhatsAppPhoneNumbers/WhatsAppPhoneNumbers.api';
import { useWhatsAppSetupStatus } from '@/Domain/hooks/Integration/useWhatsAppSetupStatus.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

const SESSION_STORAGE_KEY = 'qc_whatsapp_health_toast_shown';

// sessionStorage throws in private-mode / blocked-cookie browsers. Treat any
// failure as "nothing shown yet" so the dedup degrades gracefully instead of
// throwing from render; the in-memory shownRef still dedups within a mount.
function readShownKey(): string | null {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeShownKey(key: string): void {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, key);
  } catch {
    // storage unavailable — nothing to persist; shownRef covers this mount.
  }
}

/**
 * Surfaces a dashboard toast when the WhatsApp channel is in a customer-actionable
 * error state (send_error on the status endpoint), pointing the user to the
 * WhatsApp settings modal. Deduped per error instance for the session so it
 * notifies once, not on every navigation.
 *
 * `enabled` should reflect whether WhatsApp is relevant for this account (e.g.
 * the plan allows it) so non-WhatsApp users don't trigger the status endpoint,
 * which makes a live Meta ping for connected accounts. Reuses the shared
 * useWhatsAppSetupStatus query so the Inbox/modal cache is reused, not re-fetched.
 */
export function useWhatsAppChannelHealthToast(enabled: boolean): void {
  const { t } = useTranslation(['integrations']);
  const { toggleModal } = useModalStore();
  const shownRef = useRef<string | null>(null);

  const { data } = useWhatsAppSetupStatus(enabled);
  const sendError: WhatsAppSendHealthError | null = data?.send_error ?? null;

  useEffect(() => {
    if (!sendError) return;

    const key = `${sendError.category}:${sendError.occurred_at ?? ''}`;
    if (shownRef.current === key) return;
    if (readShownKey() === key) {
      shownRef.current = key;
      return;
    }
    shownRef.current = key;
    writeShownKey(key);

    toast.warning(t('whatsapp_phone_numbers.send_error.toast.title'), {
      description: t('whatsapp_phone_numbers.send_error.toast.description'),
      action: {
        label: t('whatsapp_phone_numbers.send_error.toast.action'),
        onClick: () => toggleModal(MODAL_NAMES.WHATSAPP_PHONE_NUMBERS_SHEET),
      },
      duration: 12000,
    });
  }, [sendError, t, toggleModal]);
}
